import { apiRequest } from "@/app/lib/api-client";
import {
  API_ERRORS,
  createSessionLogSchema,
  favouriteStateSchema,
  logActivitySchema,
  sessionLogListSchema,
  sessionLogSchema,
  type ApiResult,
  type CreateSessionLogPayload,
} from "@shared/contracts";
import { toLogActivity, toSessionLog, toSessionLogPage } from "./logs.converter";
import type { LogActivityDayModel, SessionLogModel, SessionLogPageModel } from "./logs.model";

/**
 * HOW MANY LOGS THE HISTORY CHART READS, AND WHY IT IS THE CONTRACT'S OWN CEILING.
 *
 * The chart draws seven weeks and the first stat card counts a calendar month, so both need a
 * WINDOW rather than a page — and it has to be one request, because a chart whose bars grow
 * while the list underneath it is scrolled is a chart nobody can read twice. 100 is
 * `listSessionLogsSchema`'s maximum, and seven weeks is at most ~14 sessions a week for
 * somebody training twice a day every day, so it covers the chart's whole span for every
 * athlete who exists. Where it does not — where the service says there is more after 100 rows
 * — the total stat renders `100+` rather than an exact number that is wrong.
 */
export const TREND_WINDOW = 100;

/**
 * THE WRITE PATH OF THE LOGS DOMAIN — closing a session, and the heart.
 *
 * ⚠️ THE READ PATH (history, benchmarks) IS BUD-290 AND ADDS ITS METHODS BESIDE THESE. One
 * service module per domain, not one per screen: two modules would be two ways to reach one
 * set of endpoints, and the barrel exports neither of them.
 *
 * 🔑 THE DEBRIEF WRITE TAKES NO ID, AND THAT IS THE SERVICE'S DESIGN RATHER THAN AN OMISSION
 * HERE. `api/src/domains/logs/routes.ts` says it: a session that was STARTED already has a row
 * opened at `started` against its request id, and this action FILLS THAT ROW IN rather than
 * writing a second beside it. The join key is `requestId` in the payload — which is why the
 * debrief goes to the trouble of carrying one — and a session with no request id is an insert.
 * So the 201/200 the service answers with is a real distinction and not a formality.
 */
export const logsService = {
  /**
   * HISTORY, newest day first, one page at a time — added by BUD-290.
   *
   * ONE METHOD PER USE CASE, so this is not `list({ favouritesOnly })`: a flag that decides
   * WHICH rows come back is a flag somebody eventually passes wrong, and nothing about the
   * call site would look like a leak. `benchmarks` below asks the service's own named path.
   */
  async history(cursor?: string | null): Promise<ApiResult<SessionLogPageModel>> {
    return readPage(`/v1/logs${cursorQuery(cursor)}`);
  },

  /**
   * THE BENCHMARKS — the hearted subset, under the name the design uses for it.
   *
   * 🔑 THE SAME CONCEPT AS `favourite` ABOVE, READ BACK. `GET /v1/logs/benchmarks` is
   * `GET /v1/logs?favouritesOnly=true` on the far side; there is one table and no
   * `is_benchmark` flag beside the heart, which is why this is a path and not a second store.
   */
  async benchmarks(cursor?: string | null): Promise<ApiResult<SessionLogPageModel>> {
    return readPage(`/v1/logs/benchmarks${cursorQuery(cursor)}`);
  },

  /**
   * ONE LOGGED SESSION, WHOLE — with its splits, which the list deliberately omits.
   *
   * The list's shape is `sessionLogSchema.omit({ splits: true })` on both sides, because a
   * two-line history row draws none of them and a fifty-round EMOM would ship fifty rows per
   * item. The screen that opens off a row draws every one of them, so it asks for one log.
   */
  async detail(id: string): Promise<ApiResult<SessionLogModel>> {
    const result = await apiRequest<unknown>(`/v1/logs/${id}`);
    if (!result.ok) return result;
    const parsed = sessionLogSchema.safeParse(result.data);
    if (!parsed.success) return { ok: false, error: API_ERRORS.VALIDATION_FAILED, message: "shape" };
    return { ok: true, data: toSessionLog(parsed.data) };
  },

  /**
   * A YEAR OF TRAINING DAYS, for the grid at the top of history.
   *
   * One aggregate row per day rather than a page of logs: the grid needs every day of a year and
   * none of what a log carries, and asking the list for a year of sessions would be megabytes to
   * draw three hundred squares.
   */
  async activity(): Promise<ApiResult<LogActivityDayModel[]>> {
    const result = await apiRequest<unknown>("/v1/logs/activity");
    if (!result.ok) return result;

    const parsed = logActivitySchema.safeParse(result.data);
    if (!parsed.success) return { ok: false, error: API_ERRORS.SERVER_ERROR, message: "shape" };
    return { ok: true, data: toLogActivity(parsed.data) };
  },

  /** The window the chart and the two stat cards are computed from. One request, no cursor. */
  async trend(): Promise<ApiResult<SessionLogPageModel>> {
    return readPage(`/v1/logs?limit=${TREND_WINDOW}`);
  },

  /**
   * CLOSE THE SESSION. One call, and it writes the log row AND the engine's completion record
   * in one transaction on the far side.
   *
   * VALIDATED BEFORE IT IS SENT, against the same export the service validates it with. A
   * payload the service would refuse is refused here, on the phone, rather than costing a
   * round trip on a gym's wifi to be told — which on a phone is the difference between a
   * button that failed and a button that appears not to have worked.
   */
  async create(payload: CreateSessionLogPayload): Promise<ApiResult<SessionLogModel>> {
    const body = createSessionLogSchema.safeParse(payload);
    if (!body.success) return { ok: false, error: API_ERRORS.VALIDATION_FAILED, message: "shape" };

    const result = await apiRequest<unknown>("/v1/logs", { method: "POST", body: body.data });
    if (!result.ok) return result;

    const parsed = sessionLogSchema.safeParse(result.data);
    if (!parsed.success) return { ok: false, error: API_ERRORS.SERVER_ERROR, message: "shape" };
    return { ok: true, data: toSessionLog(parsed.data) };
  },

  /**
   * THE HEART, PRESSED — which is what "save as benchmark" IS.
   *
   * 🔑 ONE CONCEPT, ONE ENDPOINT. The design calls a kept session a benchmark and the data
   * calls it a favourite; there is no `is_benchmark` flag beside the heart and there must not
   * be one. `GET /v1/logs/benchmarks` is `GET /v1/logs?favouritesOnly=true` under the name the
   * design uses, and this is the write behind both.
   *
   * Idempotent by the table's own constraint: favouriting twice is favouriting once, so a
   * double tap is a success rather than a conflict.
   */
  async favourite(id: string): Promise<ApiResult<boolean>> {
    const result = await apiRequest<unknown>(`/v1/logs/${encodeURIComponent(id)}/favourite`, {
      method: "POST",
    });
    if (!result.ok) return result;

    const parsed = favouriteStateSchema.safeParse(result.data);
    if (!parsed.success) return { ok: false, error: API_ERRORS.SERVER_ERROR, message: "shape" };
    return { ok: true, data: parsed.data.isFavourite };
  },

  /**
   * THE HEART, RELEASED. A DELETE and not a second POST carrying a flag, because the join row
   * is the whole of the state: there is nothing to update, only a row that is there or is not.
   */
  async unfavourite(id: string): Promise<ApiResult<boolean>> {
    const result = await apiRequest<unknown>(`/v1/logs/${encodeURIComponent(id)}/favourite`, {
      method: "DELETE",
    });
    if (!result.ok) return result;

    const parsed = favouriteStateSchema.safeParse(result.data);
    if (!parsed.success) return { ok: false, error: API_ERRORS.SERVER_ERROR, message: "shape" };
    return { ok: true, data: parsed.data.isFavourite };
  },
};

function cursorQuery(cursor?: string | null): string {
  return cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
}

/**
 * ONE PAGE OF HISTORY, PARSED RATHER THAN CAST — the shape all three reads answer with.
 *
 * A field the service renames has to fail HERE, at the boundary, and not as
 * `undefined.toUpperCase()` inside a list row, which on a phone is a white screen with no
 * console anybody can read.
 */
async function readPage(path: string): Promise<ApiResult<SessionLogPageModel>> {
  const result = await apiRequest<unknown>(path);
  if (!result.ok) return result;

  const parsed = sessionLogListSchema.safeParse(result.data);
  if (!parsed.success) return { ok: false, error: API_ERRORS.SERVER_ERROR, message: "shape" };
  return { ok: true, data: toSessionLogPage(parsed.data) };
}
